// Background enrichment runner — module singleton, so a batch keeps polling
// across navigation with no blocking UI. Progress and results surface as
// bottom-right toasts; per-row "Enriched" marks land live via enrichedLeads.
import { startEnrich, enrichStatus, saveActiveBatch, clearActiveBatch, MAX_ENRICH_ROWS } from './enrich.js'
import { keyOf } from './companyMeta.js'
import { markEnriched, setEnriching, clearEnriching } from './enrichedLeads.js'
import { saveEnrichment } from './enrichment.js'
import { showToast } from './toast.js'
import { logEvent } from './events.js'
import { recordEnrich } from './intel.js'
import { currentScope } from './userScope.js'

const POLL_MS = 3000
const MAX_POLL_FAILS = 5

const active = { timer: null, batchId: null }
// Job ids already written to the intel timeline. Module scope, not per-poll:
// resumeEnrichment() re-polls a finished batch, and a per-call Set let it
// re-log every job — duplicate timeline entries plus a reset freshness clock.
const recordedJobs = new Set()
// Claimed synchronously on entry to runEnrichment. `active.timer` is only set
// once poll() starts, i.e. after `await startEnrich`, so it cannot stop a second
// click landing during that window — and each click is a fully paid batch.
let starting = false

export function enrichmentRunning() {
  return Boolean(active.timer)
}

// "A enriched" · "A and B enriched" · "A, B and 8 more enriched"
function summarize(names) {
  if (names.length === 1) return names[0]
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  if (names.length === 3) return `${names[0]}, ${names[1]} and ${names[2]}`
  return `${names[0]}, ${names[1]} and ${names.length - 2} more`
}

function stop() {
  clearInterval(active.timer)
  active.timer = null
  active.batchId = null
}

function poll(batchId, rows, category = '') {
  active.batchId = batchId
  // The account this batch belongs to. A poller is a module singleton driven by
  // setInterval: it outlives the React tree, so signing out does not stop it.
  // Everything a completed job writes — the dossier (enrichment.js), the
  // "enriched" mark, the intel record — resolves its localStorage key against
  // whoever is signed in AT WRITE TIME. Without this guard, A starting a batch
  // and B signing in before it lands delivered A's paid research into B's
  // stores under B's name.
  const owner = currentScope()
  const keyByName = new Map((rows || []).map((r) => [r.name, keyOf(r)]))
  // Rows show "Enriching…" from the moment the batch starts until their own
  // job reports back — so the table reflects work in progress, not silence.
  setEnriching((rows || []).map((r) => keyOf(r)))
  // recordEnrich() keys off a row object, so keep the rows reachable by key.
  const rowByKey = new Map((rows || []).map((r) => [keyOf(r), r]))
  let fails = 0
  const tick = async () => {
    // The signed-in account changed under us. Drop the batch rather than write
    // it into someone else's stores. Deliberately WITHOUT clearActiveBatch():
    // that key now resolves to the new account, and the handle has to stay
    // under the owner's key so they can re-attach when they sign back in.
    if (currentScope() !== owner) {
      stop()
      clearEnriching() // we are no longer polling — and these are not their rows
      return
    }
    let res
    try {
      res = await enrichStatus(batchId)
      fails = 0
    } catch {
      fails += 1
      if (fails >= MAX_POLL_FAILS) {
        stop()
        clearEnriching() // we stopped polling; we can no longer claim progress
        showToast({
          tone: 'error',
          title: 'Lost contact with enrichment',
          body: 'The batch may still finish server-side — press Enrich again to re-attach.',
          duration: 8000,
        })
      }
      return
    }
    // Re-check after the round-trip: the sign-out could have landed while the
    // status request was in flight, and every write below is what would then
    // go to the wrong account.
    if (currentScope() !== owner) {
      stop()
      clearEnriching()
      return
    }
    const jobs = res.jobs || []
    // The server echoes the row key we sent (j.key); the name map is only a
    // fallback for batches started before that existed. Without this, a batch
    // re-attached after a reload (no rows in hand) could never mark anything.
    // Any job that has settled (done or error) is no longer in flight.
    clearEnriching(
      jobs
        .filter((j) => j.status === 'done' || j.status === 'error')
        .map((j) => j.key || keyByName.get(j.company))
        .filter(Boolean),
    )
    const doneJobs = jobs.filter((j) => j.status === 'done')
    const doneKeys = doneJobs.map((j) => j.key || keyByName.get(j.company)).filter(Boolean)
    // Keep what the research actually found — this is the whole point of the
    // spend, and the lead page reads it straight from here.
    for (const j of doneJobs) {
      const k = j.key || keyByName.get(j.company)
      if (!k) continue
      if (!recordedJobs.has(j.jobId)) {
        recordedJobs.add(j.jobId)
        // Per-lead timeline ("Enriched with AI research").
        // recordEnrich() keys by keyOf(row): a bare name string yields no key
        // and the call silently no-ops, so hand it the row (or a stand-in
        // carrying the key we already resolved).
        recordEnrich([rowByKey.get(k) || { company_id: k, name: j.company }], category)
      }
      saveEnrichment(k, {
        company: j.company || '',
        result: j.result || {},
        rankedScore: j.rankedScore ?? null,
        phones: j.phones || {},
        contactsNote: j.contactsNote || '',
        cached: Boolean(j.cached),
        // A cache hit returns research up to 90 days old. Dating it "now" made
        // the 24h rule protect stale findings and the re-run dialog describe a
        // months-old dossier as an hour old.
        enrichedAt: (j.cached && j.cachedFrom) || new Date().toISOString(),
      })
    }
    markEnriched(doneKeys)
    if (res.complete) {
      stop()
      clearActiveBatch()
      clearEnriching() // nothing is in flight once the batch is complete
      const done = jobs.filter((j) => j.status === 'done').map((j) => j.company)
      const failed = jobs.length - done.length
      if (done.length) {
        // Also lands in the notifications bell + the events log.
        logEvent('leads_ai_enriched', {
          detail: `${summarize(done)} enriched — ${done.length} compan${done.length === 1 ? 'y' : 'ies'} researched and scored${failed ? `, ${failed} failed` : ''}`,
        })
        showToast({
          tone: 'success',
          title: `${summarize(done)} enriched`,
          body: `${done.length} compan${done.length === 1 ? 'y' : 'ies'} researched and scored${failed ? ` · ${failed} failed` : ''} — open a lead to see the results.`,
          duration: 9000,
        })
      } else {
        logEvent('leads_ai_enriched', {
          detail: `Enrichment failed — ${jobs.length || 'no'} compan${jobs.length === 1 ? 'y' : 'ies'} could not be researched`,
        })
        showToast({
          tone: 'error',
          title: 'Enrichment failed',
          body: 'No companies could be enriched — check the OpenAI key and try again.',
          duration: 8000,
        })
      }
    }
  }
  tick()
  active.timer = setInterval(tick, POLL_MS)
}

// Fire-and-forget: starts the batch and narrates via toasts. Callers should
// pre-filter already-enriched rows (enrichedLeads) before calling.
export async function runEnrichment(rows, category) {
  if (starting) return
  if (active.timer) {
    showToast({
      tone: 'warn',
      title: 'Enrichment already running',
      body: 'One batch at a time — results land as toasts when it finishes.',
    })
    return
  }
  const capped = rows.slice(0, MAX_ENRICH_ROWS)
  // Who is paying for this batch, captured before the round-trip below.
  const owner = currentScope()
  let started
  starting = true
  try {
    started = await startEnrich(capped, category)
  } catch (err) {
    // Never blame "the deployed API" for everything: the status tells us which
    // of these it actually was, and the server's own message is more useful
    // than anything invented here.
    const status = err?.status
    const title =
      status === 401 || status === 403
        ? 'Sign in again to enrich'
        : status === 409
          ? "Enrichment isn't set up yet"
          : status === 400
            ? "That lead can't be enriched"
            : status
              ? `Enrichment failed (${status})`
              : "Couldn't reach enrichment"
    showToast({
      tone: 'warn',
      title,
      body:
        err?.message ||
        'The enrichment API did not respond — check your connection and try again.',
      duration: 9000,
    })
    return
  } finally {
    starting = false
  }
  if (started.configured === false) {
    showToast({
      tone: 'warn',
      title: "Enrichment isn't configured",
      body: 'Add the OpenAI (and Apollo) keys to Secrets Manager, then try again.',
      duration: 8000,
    })
    return
  }
  // A sign-out/sign-in can land inside the startEnrich round-trip. Saving the
  // handle now would file the owner's in-flight batch under the NEW account's
  // key, and polling would deliver the results there too. Abandon it instead:
  // the batch finishes server-side either way, and nobody else inherits it.
  if (currentScope() !== owner) return
  saveActiveBatch(started.batchId, category)
  showToast({
    tone: 'progress',
    title: `Enriching ${capped.length} compan${capped.length === 1 ? 'y' : 'ies'} in the background`,
    body: 'Keep working — rows badge up as results land, and a toast recaps when it finishes.',
    duration: 6000,
  })
  poll(started.batchId, capped, category)
}

// Re-attach to a batch that was started before a reload ("Run in background").
export function resumeEnrichment(batchId) {
  if (active.timer) return
  showToast({
    tone: 'progress',
    title: 'Enrichment still running',
    body: 'Re-attached to the in-flight batch — results land as a toast when done.',
  })
  poll(batchId, [])
}
